import { AnimatePresence, motion } from 'framer-motion'
import { rem } from 'polished'
import React from 'react'
import styled from 'styled-components'
import { Box, Container, Flex } from 'components/Layout'
import { Text } from 'components/Typography'
import { COLOR_INTENT, FONT_STACK, FONT_WEIGHT, SPACE } from 'Theme'
import {
  PERKS_DISCLAIMER,
  TRADE_CHANNELS,
  TRADE_PERK_CATEGORIES,
} from '../tradePerksData'
import { CATEGORY_ICONS, ChevronDownIcon } from './tradeIcons'

const { TRADE_PAGE } = COLOR_INTENT.WIDGETS

const TRANSITION_DURATION = 0.2

const IntroText = styled(Text)`
  max-width: ${rem(640)};
`

const ChannelChip = styled.a`
  display: flex;
  align-items: center;
  gap: ${rem(9)};
  background: ${TRADE_PAGE.SECTION_BG};
  border: 1px solid ${TRADE_PAGE.BORDER_CHIP};
  border-radius: 4px;
  padding: ${rem(10)} ${rem(18)};
  text-decoration: none;

  &:hover {
    border-color: ${TRADE_PAGE.ACCENT};
  }
`

const ChannelDot = styled.span`
  flex: 0 0 auto;
  width: ${rem(7)};
  height: ${rem(7)};
  border-radius: 50%;
  background: ${TRADE_PAGE.CTA_BG};
`

const AccordionList = styled(Box)`
  max-width: ${rem(760)};
`

const AccordionItem = styled(Box)`
  border-top: 1px solid ${TRADE_PAGE.BORDER_SAND};
`

const AccordionHeader = styled.button`
  display: flex;
  align-items: center;
  gap: ${rem(18)};
  width: 100%;
  min-height: ${rem(44)};
  padding: ${rem(20)} ${rem(4)};
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: inherit;
`

const AccordionIcon = styled.span`
  flex: 0 0 auto;
  width: ${rem(30)};
  height: ${rem(30)};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${TRADE_PAGE.ACCENT};
`

const AccordionTitle = styled(Text)`
  flex: 1;
`

const AccordionChevron = styled.span<{ $isOpen: boolean }>`
  flex: 0 0 auto;
  display: flex;
  color: ${TRADE_PAGE.CHEVRON};
  transform: rotate(${({ $isOpen }) => ($isOpen ? '180deg' : '0deg')});
  transition: transform ${TRANSITION_DURATION}s;
`

const AccordionBody = styled(Flex)`
  flex-direction: column;
  gap: ${rem(16)};
  padding: 0 ${rem(4)} ${rem(22)} ${rem(48)};
`

export const ShopAnywhereSection: React.FC = () => {
  const [openCategories, setOpenCategories] = React.useState<
    Record<string, boolean>
  >({ Spend: true })

  const toggleCategory = (key: string) => {
    setOpenCategories((previous) => ({ ...previous, [key]: !previous[key] }))
  }

  return (
    <Box
      backgroundColor={TRADE_PAGE.PAGE_BG}
      style={{ borderTop: `1px solid ${TRADE_PAGE.BORDER}` }}
    >
      <Container
        py={{ MOBILE: rem(52), TABLET: rem(84) }}
        px={{ MOBILE: SPACE.PX_24, TABLET: SPACE.PX_40 }}
      >
        <Text
          as="h2"
          fontFamily={FONT_STACK.SERIF}
          fontWeight={FONT_WEIGHT.NORMAL}
          fontSize={{ MOBILE: rem(28), TABLET: rem(34) }}
          color={TRADE_PAGE.HEADING}
          mb={SPACE.PX_6}
        >
          Shop anywhere. Earn everywhere.
        </Text>
        <IntroText
          as="p"
          fontSize={{ MOBILE: rem(14), TABLET: rem(15) }}
          lineHeight="1.6"
          color={TRADE_PAGE.TEXT_MUTED}
          mb={{ MOBILE: rem(18), TABLET: rem(28) }}
        >
          Your perks travel with you—whether you&apos;re shopping in-store, on a
          brand site, or through The Expert, every purchase counts toward the
          same rewards. Here&apos;s everything they unlock, organized by what it
          does for your firm.
        </IntroText>

        <Flex flexWrap="wrap" gap={rem(12)} mb={rem(16)}>
          {TRADE_CHANNELS.map((channel) => (
            <ChannelChip
              key={channel.name}
              href={channel.href}
              target="_blank"
              rel="noopener"
            >
              <ChannelDot />
              <Text
                as="span"
                fontSize={rem(13)}
                fontWeight={FONT_WEIGHT.MEDIUM}
                color={TRADE_PAGE.TEXT}
              >
                {channel.name}
              </Text>
            </ChannelChip>
          ))}
        </Flex>

        <Text
          as="p"
          fontFamily={FONT_STACK.SERIF}
          fontStyle="italic"
          fontSize={rem(13)}
          color={TRADE_PAGE.EYEBROW}
          mb={rem(34)}
        >
          {PERKS_DISCLAIMER}
        </Text>

        <AccordionList>
          {TRADE_PERK_CATEGORIES.map((category) => {
            const isOpen = Boolean(openCategories[category.key])
            const Icon = CATEGORY_ICONS[category.icon]
            const headerId = `shop-anywhere-header-${category.key}`
            const panelId = `shop-anywhere-panel-${category.key}`

            return (
              <AccordionItem key={category.key}>
                <AccordionHeader
                  type="button"
                  id={headerId}
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  onClick={() => toggleCategory(category.key)}
                >
                  <AccordionIcon>
                    <Icon size={27} />
                  </AccordionIcon>
                  <AccordionTitle
                    fontFamily={FONT_STACK.SERIF}
                    fontSize={rem(22)}
                    color={TRADE_PAGE.DARK_BG}
                  >
                    {category.titleLead} <em>{category.titleEmphasis}</em>
                  </AccordionTitle>
                  <AccordionChevron $isOpen={isOpen}>
                    <ChevronDownIcon size={20} />
                  </AccordionChevron>
                </AccordionHeader>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial="collapsed"
                      animate="open"
                      exit="collapsed"
                      style={{ overflow: 'hidden' }}
                      variants={{
                        open: { opacity: 1, height: 'auto' },
                        collapsed: { opacity: 0, height: 0 },
                      }}
                      transition={{ duration: TRANSITION_DURATION }}
                    >
                      <AccordionBody
                        id={panelId}
                        role="region"
                        aria-labelledby={headerId}
                      >
                        {category.perks.map((perk) => (
                          <Box key={perk.name}>
                            <Text
                              as="p"
                              fontSize={rem(14.5)}
                              fontWeight={FONT_WEIGHT.SEMI_BOLD}
                              color={TRADE_PAGE.TEXT_DARK}
                              mb={0}
                            >
                              {perk.name}
                            </Text>
                            <Text
                              as="p"
                              fontSize={rem(13)}
                              color={TRADE_PAGE.TEXT_SOFT}
                              lineHeight="1.5"
                              mt={rem(2)}
                              mb={0}
                            >
                              {perk.desc}
                            </Text>
                          </Box>
                        ))}
                      </AccordionBody>
                    </motion.div>
                  )}
                </AnimatePresence>
              </AccordionItem>
            )
          })}
          <AccordionItem />
        </AccordionList>
      </Container>
    </Box>
  )
}
